import { CookieHinweis, openCookieHinweis } from "./CookieHinweis";
import { useEffect, useState, type ReactNode } from "react";
import { api } from "../api";
import { Link, NavLink, useLocation } from "react-router";
import { StartBanner } from "./Status";
import { modeFor } from "../mode";

export function Logo({ to = "/" }: { to?: string }) {
  return (
    <Link className="logo" to={to} aria-label="HandVero Startseite">
      <svg width="34" height="30" viewBox="0 0 34 30" aria-hidden="true">
        <path d="M23 3H2.5V27.5H27V15" fill="none" stroke="currentColor" strokeWidth="3" />
        <path d="M6 15.5l3.8-1.3 3.7 3.6L32 1.5 13.6 25.5z" fill="currentColor" />
      </svg>
      <span>
        <span className="hand">Hand</span>
        <span className="vero">Vero</span>
      </span>
    </Link>
  );
}

const NAV = [
  ["/funktionen", "Funktionen"],
  ["/web-app", "Web-App"],
  ["/preise", "Preise"],
  ["/ueber-uns", "Über uns"],
  ["/kontakt", "Kontakt"],
] as const;

const NAV_PRIVAT = [
  ["/privat", "Hausakte"],
  ["/privat/erklaert", "Vero erklärt"],
  ["/privat/preise", "Preise"],
  ["/datensicherheit", "Ihre Daten"],
  ["/kontakt", "Kontakt"],
] as const;

export function Layout({ children }: { children: ReactNode }) {
  const { pathname, hash } = useLocation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [loggedIn, setLoggedIn] = useState(false);
  const [hasBlog, setHasBlog] = useState(false);
  const privat = modeFor(pathname);

  // Optik wechseln: Hellblau für Privatpersonen (siehe styles.css)
  useEffect(() => {
    document.documentElement.dataset.mode = privat ? "privat" : "betrieb";
    return () => {
      delete document.documentElement.dataset.mode;
    };
  }, [privat]);

  useEffect(() => {
    api.get<{ loggedIn: boolean }>("/api/auth/status").then((s) => setLoggedIn(s.loggedIn), () => undefined);
  }, [pathname]);

  useEffect(() => {
    api.get<unknown[]>("/api/blog").then((p) => setHasBlog(p.length > 0), () => undefined);
  }, []);

  useEffect(() => {
    setOpen(false);
    if (!hash) window.scrollTo(0, 0);
  }, [pathname, hash]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <StartBanner />
      <div className="mode-strip">
        <div className="wrap">
          <span className="mode-label">Ich bin</span>
          <div className="mode-switch" role="group" aria-label="Ansicht wählen">
            <Link to="/" aria-current={privat ? undefined : "true"} className={privat ? "" : "on"}>
              Betrieb / Selbstständig
            </Link>
            <Link to="/privat" aria-current={privat ? "true" : undefined} className={privat ? "on" : ""}>
              Privat / Zuhause
            </Link>
          </div>
        </div>
      </div>
      <header className={`site${scrolled ? " scrolled" : ""}`}>
        <div className="wrap">
          <Logo to={privat ? "/privat" : "/"} />
          <nav className={`main${open ? " open" : ""}`} id="nav" aria-label="Hauptmenü">
            {(privat ? NAV_PRIVAT : [...NAV, ...(hasBlog ? ([["/blog", "Blog"]] as const) : [])]).map(([to, label]) => (
              <NavLink key={to} to={to}>
                {label}
              </NavLink>
            ))}
          </nav>
          <div className="head-actions">
            {loggedIn ? (
              <>
                <Link className="btn btn-ghost btn-small" to="/kundenbereich">
                  Kundenbereich
                </Link>
                <Link className="btn btn-primary btn-small" to="/desktop">
                  <span className="long">HandVero öffnen</span>
                  <span className="short">Öffnen</span>
                </Link>
              </>
            ) : (
              <Link className="btn btn-primary btn-small btn-login" to={privat ? "/anmelden" : "/login"}>
                Login
              </Link>
            )}
            <button className="btn btn-ghost btn-small menu-btn" aria-expanded={open} aria-controls="nav" onClick={() => setOpen((o) => !o)}>
              Menü
            </button>
          </div>
        </div>
      </header>
      <main>{children}</main>
      <CookieHinweis />
      <footer className="site">
        <div className="wrap">
          <span>© {new Date().getFullYear()} HandVero · Elterlein</span>
          <nav aria-label="Rechtliches">
            <Link to="/rechtliches#impressum">Impressum</Link>
            <Link to="/datensicherheit">Ihre Daten</Link>
            <Link to="/rechtliches#datenschutz">Datenschutz</Link>
            <button type="button" className="linklike" onClick={openCookieHinweis}>
              Cookie-Hinweis
            </button>
            <Link to="/rechtliches#agb">AGB</Link>
            <Link to="/privat/agb">AGB Privat</Link>
            <Link to="/rechtliches#avv">Auftragsverarbeitung</Link>
            <Link to="/stand">Stand heute</Link>
          </nav>
        </div>
      </footer>
    </>
  );
}
